import { useEffect, useState, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import Dashboard from "./pages/Dashboard";
import Analysis from "./pages/Analysis";
import Recommendations from "./pages/Recommendations";
import History from "./pages/History";
import PricingPage from "./pages/PricingPage";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import SubscriptionManagement from "./pages/SubscriptionManagement";
import LotteryHistory from "./pages/LotteryHistory";
import SuperIA from "./pages/SuperIA";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminLotteries from "./pages/admin/AdminLotteries";
import AdminResults from "./pages/admin/AdminResults";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminAnalista from "./pages/admin/AdminAnalista";
import AdminSystem from "./pages/admin/AdminSystem";
import AdminTransactions from "./pages/admin/AdminTransactions";
import AdminAffiliates from "./pages/admin/AdminAffiliates";
import AdminSuperIAMaster from "./pages/admin/AdminSuperIAMaster";
import AdminUserActivity from "./pages/admin/AdminUserActivity";
import AdminPredictions from "./pages/admin/AdminPredictions";
import AdminCoupons from "./pages/admin/AdminCoupons";
import AdminCreditOrders from "./pages/admin/AdminCreditOrders";
import CreditsPage from "./pages/Credits";
import AffiliateDashboard from "./pages/AffiliateDashboard";
import BotDiario from "./pages/BotDiario";
import AriesPage from "./pages/Aries";
import PromotionsPage from "./pages/Promotions";
import SevenIAPage from "./pages/SevenIA";
import PowerNumberPage from "./pages/PowerNumber";
import AccuracyDashboard from "./pages/AccuracyDashboard";
import PaleTripletaPage from "./pages/PaleTripleta";
import JugadaDelDia from "./pages/JugadaDelDia";
import PrometeoPage from "./pages/Prometeo";
import ConsensoPage from "./pages/Consenso";
import TauroPage from "./pages/Tauro";
import TauroPerformancePage from "./pages/TauroPerformance";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Global axios interceptor: HTTP 402 → redirect to /pricing (paywall) or /credits (no credits)
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 402) {
      const path = window.location.pathname;
      const detail = error.response?.data?.detail;
      const code = typeof detail === 'object' ? detail?.code : null;

      // No credits → redirect to /credits page (where user can buy more)
      if (code === 'NO_CREDITS') {
        if (path !== '/credits' && path !== '/pricing') {
          const required = detail?.required || 10;
          toast.error(`Sin créditos suficientes. Necesitas ${required} créditos para usar esta función.`, {
            duration: 4500,
            action: { label: 'Comprar créditos', onClick: () => { window.location.href = '/credits'; } },
          });
          setTimeout(() => { window.location.href = '/credits'; }, 1500);
        }
      } else {
        // No subscription / classic paywall → redirect to /pricing
        if (path !== '/pricing' && path !== '/' && path !== '/subscription/success' && path !== '/credits') {
          toast.error('Necesitas una suscripcion activa para usar esta funcion', {
            duration: 4000,
            action: { label: 'Suscribirme', onClick: () => { window.location.href = '/pricing'; } },
          });
          setTimeout(() => { window.location.href = '/pricing'; }, 1500);
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth Context
export const AuthContext = createContext(null);

// Theme Context
export const ThemeContext = createContext(null);

import { tokenStorage, themeStorage } from "./utils/storage";

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => themeStorage.get());

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    themeStorage.set(theme);
  }, [theme]);

  const toggleTheme = () => setTheme(prev => prev === "dark" ? "light" : "dark");

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(tokenStorage.get());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (e) {
          tokenStorage.remove();
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    tokenStorage.set(response.data.access_token);
    setToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (email, password, name, referral_code = null) => {
    const response = await axios.post(`${API}/auth/register`, { 
      email, 
      password, 
      name,
      referral_code: referral_code || undefined
    });
    tokenStorage.set(response.data.access_token);
    setToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = () => {
    tokenStorage.remove();
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (adminOnly && !user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Register redirect - preserves ?ref= param for referral tracking
const RegisterRedirect = () => {
  const [searchParams] = useSearchParams();
  const ref = searchParams.get('ref');
  return <Navigate to={ref ? `/?ref=${ref}` : '/'} replace />;
};

function App() {
  return (
    <div className="App noise-overlay">
      <ThemeProvider>
      <Toaster 
        position="top-right" 
        richColors 
        toastOptions={{
          style: {
            background: 'var(--toast-bg, #1e293b)',
            border: '1px solid var(--toast-border, #334155)',
          }
        }}
      />
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/register" element={<RegisterRedirect />} />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/analysis/:lotteryId?" element={
              <ProtectedRoute>
                <Analysis />
              </ProtectedRoute>
            } />
            <Route path="/recommendations" element={
              <ProtectedRoute>
                <Recommendations />
              </ProtectedRoute>
            } />
            <Route path="/history" element={
              <ProtectedRoute>
                <History />
              </ProtectedRoute>
            } />
            <Route path="/super-ia" element={
              <ProtectedRoute>
                <SuperIA />
              </ProtectedRoute>
            } />
            <Route path="/affiliates" element={
              <ProtectedRoute>
                <AffiliateDashboard />
              </ProtectedRoute>
            } />
            <Route path="/bot-diario" element={
              <ProtectedRoute>
                <BotDiario />
              </ProtectedRoute>
            } />
            <Route path="/aries" element={
              <ProtectedRoute>
                <AriesPage />
              </ProtectedRoute>
            } />
            <Route path="/promotions" element={
              <ProtectedRoute>
                <PromotionsPage />
              </ProtectedRoute>
            } />
            <Route path="/7ia" element={
              <ProtectedRoute>
                <SevenIAPage />
              </ProtectedRoute>
            } />
            <Route path="/power-number" element={
              <ProtectedRoute>
                <PowerNumberPage />
              </ProtectedRoute>
            } />
            <Route path="/precision" element={
              <ProtectedRoute>
                <AccuracyDashboard />
              </ProtectedRoute>
            } />
            <Route path="/pale-tripleta" element={
              <ProtectedRoute>
                <PaleTripletaPage />
              </ProtectedRoute>
            } />
            <Route path="/jugada-del-dia" element={
              <ProtectedRoute>
                <JugadaDelDia />
              </ProtectedRoute>
            } />
            <Route path="/prometeo" element={
              <ProtectedRoute>
                <PrometeoPage />
              </ProtectedRoute>
            } />
            <Route path="/consenso" element={
              <ProtectedRoute>
                <ConsensoPage />
              </ProtectedRoute>
            } />
            <Route path="/tauro" element={
              <ProtectedRoute>
                <TauroPage />
              </ProtectedRoute>
            } />
            <Route path="/tauro-performance" element={
              <ProtectedRoute>
                <TauroPerformancePage />
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute adminOnly>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/lotteries" element={
              <ProtectedRoute adminOnly>
                <AdminLotteries />
              </ProtectedRoute>
            } />
            <Route path="/admin/results" element={
              <ProtectedRoute adminOnly>
                <AdminResults />
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute adminOnly>
                <AdminUsers />
              </ProtectedRoute>
            } />
            <Route path="/admin/analista" element={
              <ProtectedRoute adminOnly>
                <AdminAnalista />
              </ProtectedRoute>
            } />
            <Route path="/admin/system" element={
              <ProtectedRoute adminOnly>
                <AdminSystem />
              </ProtectedRoute>
            } />
            <Route path="/admin/transactions" element={
              <ProtectedRoute adminOnly>
                <AdminTransactions />
              </ProtectedRoute>
            } />
            <Route path="/admin/affiliates" element={
              <ProtectedRoute adminOnly>
                <AdminAffiliates />
              </ProtectedRoute>
            } />
            <Route path="/admin/super-ia-master" element={
              <ProtectedRoute adminOnly>
                <AdminSuperIAMaster />
              </ProtectedRoute>
            } />
            <Route path="/admin/user-activity" element={
              <ProtectedRoute adminOnly>
                <AdminUserActivity />
              </ProtectedRoute>
            } />
            <Route path="/admin/predictions" element={
              <ProtectedRoute adminOnly>
                <AdminPredictions />
              </ProtectedRoute>
            } />
            <Route path="/admin/coupons" element={
              <ProtectedRoute adminOnly>
                <AdminCoupons />
              </ProtectedRoute>
            } />
            <Route path="/admin/credits" element={
              <ProtectedRoute adminOnly>
                <AdminCreditOrders />
              </ProtectedRoute>
            } />
            <Route path="/credits" element={
              <ProtectedRoute>
                <CreditsPage />
              </ProtectedRoute>
            } />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/lottery/:lotteryId" element={
              <ProtectedRoute>
                <LotteryHistory />
              </ProtectedRoute>
            } />
            <Route path="/subscription/success" element={
              <ProtectedRoute>
                <SubscriptionSuccess />
              </ProtectedRoute>
            } />
            <Route path="/subscription/manage" element={
              <ProtectedRoute>
                <SubscriptionManagement />
              </ProtectedRoute>
            } />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
